import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { Card, Button, Input, Table, Space, Tag, Select, Badge, message, Popconfirm, Tooltip } from 'antd';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import PersianDatePicker from '../../components/PersianDatePicker';
import { getLast30DaysRange } from '../../utils/dateUtils';
import { ordersApi } from '../../utils/api';
import ClientSelect from '../../components/common/ClientSelect';
import { useResourcePermissions } from '../../hooks/useResourcePermissions';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { useListReturnState } from '../../hooks/useListReturnState';
const { Option } = Select;

const statusMeta = {
  pending: { text: 'در انتظار', color: 'gold' },
  processing: { text: 'در حال پردازش', color: 'blue' },
  completed: { text: 'تکمیل‌شده', color: 'green' },
  failed: { text: 'ناموفق', color: 'red' },
  'on-hold': { text: 'در حالت انتظار', color: 'cyan' },
  canceled: { text: 'لغوشده', color: 'default' },
};
const statusOptions = ['pending', 'on-hold', 'failed', 'processing', 'completed', 'canceled'];

const toFilterDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value.replace(/\//g, '-');
  if (value?.format && typeof value.format === 'function') return value.format('jYYYY/MM/DD').replace(/\//g, '-');
  return null;
};

const Orders = () => {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDeleteRecord } = useResourcePermissions('orders');
  const defaultRange = useMemo(() => getLast30DaysRange('jYYYY-jMM-jDD'), []);
  const [searchValue, setSearchValue] = useState('');
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [customer, setCustomer] = useState('all');
  const [projectId, setProjectId] = useState('all');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0, last_page: 1 });
  const [filterOptions, setFilterOptions] = useState({ clients: [], projects: [], bank_details: [] });
  const [actionLoadingKey, setActionLoadingKey] = useState(null);

  const captureCurrentListState = useCallback(
    () => ({
      searchValue,
      startDate,
      endDate,
      customer,
      projectId,
      status,
      pagination: {
        current_page: pagination.current_page,
        per_page: pagination.per_page,
      },
    }),
    [
      searchValue,
      startDate,
      endDate,
      customer,
      projectId,
      status,
      pagination.current_page,
      pagination.per_page,
    ],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.searchValue != null) setSearchValue(restored.searchValue);
    if (restored.startDate != null) setStartDate(restored.startDate);
    if (restored.endDate != null) setEndDate(restored.endDate);
    if (restored.customer != null) setCustomer(restored.customer);
    if (restored.projectId != null) setProjectId(restored.projectId);
    if (restored.status != null) setStatus(restored.status);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current_page: restored.pagination.current_page || 1,
        per_page: restored.pagination.per_page || prev.per_page,
      }));
    }
  });

  const getActionKey = (id, action) => `${id}-${action}`;
  const isActionLoading = (id, action) => actionLoadingKey === getActionKey(id, action);

  const fetchOrders = async (page = pagination.current_page, perPage = pagination.per_page) => {
    setLoading(true);
    try {
      const res = await ordersApi.getAll({
        startDate,
        endDate,
        client_id: customer === 'all' ? 'all' : customer,
        project_id: projectId === 'all' ? 'all' : projectId,
        status,
        search: searchValue || '',
        page,
        per_page: perPage,
      });
      setOrders(res?.orders || []);
      setPagination(res?.pagination || { current_page: 1, per_page: 10, total: 0, last_page: 1 });
      setFilterOptions(res?.filters || { clients: [], projects: [], bank_details: [] });
    } catch (error) {
      message.error('خطا در دریافت سفارشات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(1, pagination.per_page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, customer, projectId, status, searchValue]);

  const handleAdd = () => navigate('/orders/add');

  const handleChangeStatus = async (orderId, nextStatus) => {
    const payload = { order_id: orderId, status: nextStatus };
    if (nextStatus === 'completed') {
      const bankAccountId = filterOptions.bank_details?.[0]?.id;
      if (!bankAccountId) {
        message.warning('برای وضعیت تکمیل‌شده، حساب بانکی لازم است');
        return;
      }
      payload.bank_account_id = bankAccountId;
    }
    setActionLoadingKey(getActionKey(orderId, 'status'));
    try {
      await ordersApi.changeStatus(payload);
      message.success('وضعیت سفارش بروزرسانی شد');
      fetchOrders();
    } catch (error) {
      message.error('خطا در تغییر وضعیت سفارش');
    } finally {
      setActionLoadingKey(null);
    }
  };

  const columns = [
    {
      title: 'شماره سفارش',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (text) => <span style={{ fontFamily: 'CustomFont', fontWeight: 600 }}>{text}</span>,
    },
    {
      title: 'مشتری',
      key: 'customer',
      render: (_, record) => <span style={{ fontFamily: 'CustomFont' }}>{record.client?.name_salutation || record.client?.name || '--'}</span>,
    },
    {
      title: 'پروژه',
      key: 'project',
      render: (_, record) => <span style={{ fontFamily: 'CustomFont' }}>{record.project?.project_name || '--'}</span>,
    },
    {
      title: 'جمع کل',
      dataIndex: 'total',
      key: 'total',
      render: (value) => (
        <span style={{ fontFamily: 'CustomFont' }}>{formatPriceToman(value || 0)}</span>
      ),
    },
    {
      title: 'تاریخ سفارش',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (text) => <span style={{ fontFamily: 'CustomFont' }}>{text ? new Date(text).toLocaleDateString('fa-IR') : '--'}</span>,
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const meta = statusMeta[value] || { text: value, color: 'default' };
        return (
          <Tag color={meta.color} style={{ fontFamily: 'CustomFont' }}>
            {meta.text}
          </Tag>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 220,
      align: 'center',
      render: (_, record) => (
        <div className="w-full flex justify-center">
          <Space size="small">
            <Tooltip title="مشاهده">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigateToDetail(`/orders/${record.id}`, {}, {
                    listPath: '/orders',
                    listLabel: 'سفارش‌ها',
                    entityLabel: record.order_number || record.name || `سفارش #${record.id}`,
                  })
                }
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              />
            </Tooltip>
            {canEditRecord(record) ? (
              <Tooltip title="ویرایش">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigateToDetail(`/orders/${record.id}/edit`)}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                />
              </Tooltip>
            ) : null}
            <Tooltip title="تکمیل سفارش">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                loading={isActionLoading(record.id, 'status')}
                onClick={() => handleChangeStatus(record.id, 'completed')}
              />
            </Tooltip>
            <Select
              size="small"
              value={record.status}
              style={{ width: 120 }}
              loading={isActionLoading(record.id, 'status')}
              onChange={(value) => handleChangeStatus(record.id, value)}
            >
              {statusOptions.map((key) => (
                <Option key={key} value={key}>
                  {statusMeta[key]?.text || key}
                </Option>
              ))}
            </Select>
            {canDeleteRecord(record) ? (
              <Popconfirm
                title="حذف سفارش؟"
                onConfirm={async () => {
                  setActionLoadingKey(getActionKey(record.id, 'delete'));
                  try {
                    await ordersApi.delete(record.id);
                    message.success('سفارش حذف شد');
                    fetchOrders();
                  } catch (error) {
                    message.error('خطا در حذف سفارش');
                  } finally {
                    setActionLoadingKey(null);
                  }
                }}
                okText="بله"
                cancelText="خیر"
              >
                <Tooltip title="حذف">
                  <Button type="text" danger icon={<DeleteOutlined />} loading={isActionLoading(record.id, 'delete')} />
                </Tooltip>
              </Popconfirm>
            ) : null}
          </Space>
        </div>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                  <Badge status="processing" />
                </div>
                <h1
                  className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  مدیریت سفارشات
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchOrders()}
                  loading={loading}
                  size="middle"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  بروزرسانی
                </Button>
                {canAdd ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                    size="middle"
                    className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    افزودن سفارش جدید
                  </Button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={7} />
            ) : (
              <>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
              <Input
                placeholder="برای جستجو تایپ کنید..."
                allowClear
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                prefix={<SearchOutlined className="text-gray-400" />}
                style={{ fontFamily: 'CustomFont' }}
              />
              <ClientSelect
                value={customer}
                onChange={(v) => setCustomer(v ?? 'all')}
                includeInactive
                allowClear={false}
                leadingOptions={[{ value: 'all', label: 'همه مشتریان' }]}
              />
              <Select
                value={status}
                onChange={setStatus}
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه وضعیت‌ها</Option>
                {Object.entries(statusMeta).map(([key, meta]) => (
                  <Option key={key} value={key}>
                    {meta.text}
                  </Option>
                ))}
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <PersianDatePicker value={startDate} onChange={(v) => setStartDate(toFilterDate(v))} placeholder="از تاریخ" />
                <PersianDatePicker value={endDate} onChange={(v) => setEndDate(toFilterDate(v))} placeholder="تا تاریخ" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
              <Select
                value={projectId}
                onChange={setProjectId}
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              >
                <Option value="all">همه پروژه‌ها</Option>
                {(filterOptions.projects || []).map((p) => (
                  <Option key={p.id} value={p.id}>{p.project_name}</Option>
                ))}
              </Select>
            </div>

            <Table
              columns={columns}
              dataSource={orders}
              rowKey="id"
              loading={false}
              scroll={{ x: 1200 }}
              pagination={{
                current: pagination.current_page,
                pageSize: pagination.per_page,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                onChange: (page, perPage) => fetchOrders(page, perPage),
                showTotal: (total, range) => (
                  <span style={{ fontFamily: 'CustomFont' }}>
                    نمایش <strong>{range[0]}</strong> تا <strong>{range[1]}</strong> از{' '}
                    <strong>{total}</strong> سفارش
                  </span>
                ),
                locale: { items_per_page: 'ردیف در صفحه' },
              }}
              locale={{
                emptyText: (
                  <div style={{ fontFamily: 'CustomFont', padding: '40px 0', color: '#999' }}>
                    {searchValue ? 'نتیجه‌ای یافت نشد' : 'هنوز سفارشی ثبت نشده است'}
                  </div>
                ),
              }}
              className="custom-table"
              style={{ fontFamily: 'CustomFont' }}
              rowClassName="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            />
              </>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Orders;
